import React, { useEffect } from 'react';
import styled from 'styled-components';
import type { HBTablePaginationProps } from './HBTable.types';
import HBIconButton from '../HBIconButton/HBIconButton';
import { ArrowLeft16, ArrowRight16 } from '@honeybook/icons';
import HBText from '../HBText/HBText';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { useFocusGroup } from '../../hooks/useFocusGroup';
import { vars } from '../../theme';

const MAX_PAGE_BUTTONS = 7;
const FIXED_PAGE_BUTTON_COUNT = 2; // the first and last page buttons
const DISTANCE_TO_SHOW_ELLIPSIS = Math.floor(MAX_PAGE_BUTTONS / 2);
const MAX_DYNAMIC_PAGE_BUTTONS = MAX_PAGE_BUTTONS - FIXED_PAGE_BUTTON_COUNT;
const DISTANCE_TO_END = DISTANCE_TO_SHOW_ELLIPSIS + 1;

function HBTablePagination<T>({ table }: HBTablePaginationProps<T>) {
    const {
        pagination: { pageIndex }
    } = table.getState();

    const pageCount = table.options.meta.isInitialLoading
        ? MAX_PAGE_BUTTONS
        : table.getPageCount();

    useEffect(() => {
        if (table.options.meta.isInitialLoading) {
            return;
        }
        if (pageIndex > pageCount - 1) {
            table.setPageIndex(pageCount - 1);
        }
    }, [pageIndex, pageCount, table]);

    const isFirstPage = pageIndex === 0;
    const isLastPage = pageIndex === pageCount - 1;

    const dynamicPageCount = pageCount - FIXED_PAGE_BUTTON_COUNT;
    const dynamicPageButtonsToRender = Math.min(
        dynamicPageCount,
        MAX_DYNAMIC_PAGE_BUTTONS
    );
    const hasMorePagesThanMaxVisible =
        dynamicPageCount > MAX_DYNAMIC_PAGE_BUTTONS;
    const hasMorePagesBefore =
        hasMorePagesThanMaxVisible && pageIndex > DISTANCE_TO_SHOW_ELLIPSIS;
    const hasMorePagesAfter =
        hasMorePagesThanMaxVisible && pageIndex <= pageCount - DISTANCE_TO_END;

    const testId = useBuildTestIdInternal({
        componentName: 'HBTablePagination',
        identifier: 'pagination',
        suffix: 'container'
    });

    useFocusGroup(`.${CONTAINER_CLASS_NAME} .${BUTTON_CLASS_NAME}`);

    return (
        <Container data-testid={testId} className={CONTAINER_CLASS_NAME}>
            <HBIconButton
                label=""
                testIdName="pagination-page-prev"
                buttonTheme="subtle-invisible"
                icon={ArrowLeft16}
                onClick={table.previousPage}
                disabled={isFirstPage}
                className={BUTTON_CLASS_NAME}
            />
            <PageButton
                testIdName={0}
                label={1}
                onClick={table.firstPage}
                isSelected={isFirstPage}
            />
            {Array.from({ length: dynamicPageButtonsToRender }).map(
                (_, index) => {
                    const updatedIndex = index + 1;
                    const actualIndex =
                        !hasMorePagesThanMaxVisible ||
                        pageIndex <= DISTANCE_TO_SHOW_ELLIPSIS
                            ? updatedIndex
                            : pageIndex >= pageCount - DISTANCE_TO_SHOW_ELLIPSIS
                            ? updatedIndex + pageCount - MAX_PAGE_BUTTONS
                            : updatedIndex +
                              pageIndex -
                              DISTANCE_TO_SHOW_ELLIPSIS;

                    const isSecondButtonNotSecondPage =
                        hasMorePagesBefore && index === 0;

                    const isSecondToLastButtonNotSecondToLastPage =
                        hasMorePagesAfter &&
                        index === dynamicPageButtonsToRender - 1;

                    const shouldShowEllipsis =
                        isSecondButtonNotSecondPage ||
                        isSecondToLastButtonNotSecondToLastPage;

                    const label = shouldShowEllipsis ? '...' : actualIndex + 1;
                    return (
                        <PageButton
                            key={index}
                            testIdName={updatedIndex}
                            label={label}
                            isSelected={pageIndex === actualIndex}
                            inert={shouldShowEllipsis}
                            onClick={() => table.setPageIndex(actualIndex)}
                        />
                    );
                }
            )}
            {pageCount > 1 && (
                <PageButton
                    testIdName={dynamicPageButtonsToRender + 1}
                    isSelected={isLastPage}
                    onClick={table.lastPage}
                    label={pageCount}
                />
            )}

            <HBIconButton
                label=""
                testIdName="pagination-page-next"
                buttonTheme="subtle-invisible"
                icon={ArrowRight16}
                onClick={table.nextPage}
                disabled={isLastPage}
                className={BUTTON_CLASS_NAME}
            />
        </Container>
    );
}

export default HBTablePagination;

const CONTAINER_CLASS_NAME = 'pagination-container';
const BUTTON_CLASS_NAME = 'pagination-button';

const PageButton = ({
    isSelected,
    onClick,
    label,
    inert,
    testIdName
}: {
    isSelected?: boolean;
    onClick?: () => void;
    label: string | number;
    inert?: boolean;
    testIdName: string | number;
}) => (
    <HBIconButton
        inert={inert}
        label=""
        testIdName={`pagination-page-${testIdName}`}
        buttonTheme={isSelected ? 'accent-invisible' : 'subtle-invisible'}
        icon={() => (
            <HBText type="emphasis14" color="text.tertiary">
                {label}
            </HBText>
        )}
        onClick={onClick}
        className={BUTTON_CLASS_NAME}
        isSelected={isSelected}
    />
);

// -------------------------- Styles -------------------------- //

const Container = styled.div`
    display: flex;
    justify-content: center;
    gap: 4px;
    padding: 16px;
    border-top: 1px solid ${vars.colors.stroke.tertiary};
    overflow: auto;
`;
